import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Alert, CircularProgress, Stack, Chip,
  FormControl, InputLabel, Select, MenuItem, Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { rulesAPI, transactionsAPI } from '../services/api';
import { getCategoryColor } from '../constants/categoryColors';
import Navbar from '../components/Navbar';

export default function Rules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newPattern, setNewPattern] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [recategorizing, setRecategorizing] = useState(false);

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await rulesAPI.list();
      setRules(res.data);
    } catch {
      setError('Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRules(); }, [loadRules]);

  const handleAdd = async () => {
    if (!newPattern.trim() || !newCategory.trim()) return;
    try {
      await rulesAPI.add({ pattern: newPattern.trim(), category: newCategory.trim() });
      setNewPattern('');
      setNewCategory('');
      setSuccess(`Rule added: "${newPattern.trim()}" → ${newCategory.trim()}`);
      loadRules();
    } catch (err) {
      setError('Failed to add rule: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (rule) => {
    try {
      await rulesAPI.delete(rule.pattern, rule.category);
      setSuccess(`Removed rule: "${rule.pattern}" → ${rule.category}`);
      loadRules();
    } catch (err) {
      setError('Failed to delete rule');
    }
  };

  const handleRecategorize = async () => {
    setRecategorizing(true);
    setError('');
    setSuccess('');
    try {
      const res = await transactionsAPI.recategorize();
      const { updated, total_uncategorized } = res.data;
      setSuccess(`Re-categorized ${updated} of ${total_uncategorized} uncategorized transactions.`);
    } catch (err) {
      setError('Re-categorize failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRecategorizing(false);
    }
  };

  return (
    <>
      <Navbar />
      <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Category Rules
        </Typography>

        {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {/* Add Rule */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Add Rule
            </Typography>
            <Stack direction="row" spacing={2} alignItems="flex-end" flexWrap="wrap" useFlexGap>
              <TextField
                label="Pattern / Keyword"
                size="small"
                value={newPattern}
                onChange={(e) => setNewPattern(e.target.value)}
                sx={{ minWidth: 200 }}
                helperText='e.g. "backyard", "duke energy"'
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={newCategory}
                  label="Category"
                  onChange={(e) => setNewCategory(e.target.value)}
                >
                  {[
                    'Housing', 'Utilities', 'Groceries', 'Food & Dining', 'Transport',
                    'Shopping', 'Healthcare', 'Insurance', 'Subscriptions',
                    'Entertainment', 'Travel', 'Personal Care', 'Charity/Donation',
                    'Family Support', 'Savings', 'Income', 'Credit Card Payment',
                    'Transfer', 'Fees', 'Education', 'Other', 'Uncategorized',
                  ].map(cat => (
                    <MenuItem key={cat} value={cat}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{
                          width: 10, height: 10, borderRadius: '50%',
                          bgcolor: getCategoryColor(cat, 0),
                        }} />
                        {cat}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAdd}
                disabled={!newPattern.trim() || !newCategory.trim()}
              >
                Add
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Rule List */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6" fontWeight={600}>
                Active Rules ({rules.length})
              </Typography>
              <Tooltip title="Re-run all rules on Uncategorized transactions">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={recategorizing ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                  onClick={handleRecategorize}
                  disabled={recategorizing}
                >
                  Re-categorize Uncategorized
                </Button>
              </Tooltip>
            </Box>

            {loading ? (
              <Box textAlign="center" py={3}><CircularProgress /></Box>
            ) : rules.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={3}>
                No custom rules yet. Add one above to start.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Pattern</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rules.map((rule, i) => (
                      <TableRow key={i} hover>
                        <TableCell>
                          <Chip label={rule.pattern} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={rule.category}
                            size="small"
                            sx={{ bgcolor: getCategoryColor(rule.category, i), color: 'white' }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="error" onClick={() => handleDelete(rule)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  );
}
